/**
 * Connect existing project.
 *
 * A calm technical diagnostic surface, not a celebratory onboarding wizard.
 * Each step shows the evidence behind what DCC believes, and every ambiguous or
 * high-impact mapping has to be confirmed or corrected by a person before
 * verification runs. Nothing reaches CONNECTED on the strength of a repository
 * being selected.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  api,
  ApiRequestError,
  idempotencyKey,
  type ConfirmResult,
  type DiscoveryResult,
  type MappingDecision,
} from '../lib/api';
import { Panel, StateChip } from '../components/Primitives';
import { ErrorState } from '../components/States';

type Step = 'identify' | 'discover' | 'map' | 'verify';

const STEPS: { key: Step; title: string; detail: string }[] = [
  { key: 'identify', title: 'Project identity', detail: 'A stable DCC id is created first.' },
  {
    key: 'discover',
    title: 'Read-only discovery',
    detail: 'Static analysis; no code is executed.',
  },
  { key: 'map', title: 'Mapping confirmation', detail: 'You confirm or correct every proposal.' },
  { key: 'verify', title: 'Verification', detail: 'Mandatory checks decide the connection state.' },
];

export function ConnectProject() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('identify');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);

  const [installationId, setInstallationId] = useState('installation-48213');
  const [repositories, setRepositories] = useState('Newz-Assen/beestenboel');
  const [discovery, setDiscovery] = useState<DiscoveryResult | null>(null);

  const [decisions, setDecisions] = useState<Record<string, MappingDecision>>({});
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<ConfirmResult | null>(null);

  async function run<T>(work: () => Promise<T>, onDone: (value: T) => void) {
    setBusy(true);
    setError(null);
    try {
      onDone(await work());
    } catch (err) {
      setError(err instanceof ApiRequestError ? err : (err as Error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">Project connection layer</div>
          <h1 className="topstrip__title">Connect an existing project</h1>
          <p className="small dim">
            Discovery reads files statically in a sandbox. It never executes repository code or
            build scripts, and it never presents a guess as a fact.
          </p>
        </div>
        <Link className="btn btn--sm btn--quiet" to="/integrations">
          Back to integrations
        </Link>
      </header>

      <ol className="grid grid--4" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {STEPS.map((entry, index) => {
          const currentIndex = STEPS.findIndex((s) => s.key === step);
          const state =
            index < currentIndex ? 'success' : index === currentIndex ? 'active' : 'neutral';
          return (
            <li key={entry.key} className="card card--toned" data-tone={state}>
              <div className="label">Step {index + 1}</div>
              <strong>{entry.title}</strong>
              <p className="tiny dim" style={{ marginTop: 4 }}>
                {entry.detail}
              </p>
            </li>
          );
        })}
      </ol>

      {error && <ErrorState error={error} onRetry={() => setError(null)} />}

      {step === 'identify' && (
        <Panel
          title="1 · Project identity"
          subtitle="The DCC id is created here and never derived from a name, a slug or a repository, so it survives every later rename, reconnect or repository migration."
        >
          <div className="stack" style={{ maxWidth: 520 }}>
            <label className="stack stack--tight">
              <span className="label">Project name</span>
              <input
                className="btn"
                style={{ justifyContent: 'flex-start' }}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setSlug(
                    event.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-|-$/g, ''),
                  );
                }}
              />
            </label>
            <label className="stack stack--tight">
              <span className="label">Slug</span>
              <input
                className="btn mono"
                style={{ justifyContent: 'flex-start' }}
                value={slug}
                onChange={(event) => setSlug(event.target.value)}
              />
              <span className="tiny dim">
                A slug is a mutable label. Renaming it later does not change the project identity.
              </span>
            </label>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || name.trim().length < 2 || slug.length < 2}
              onClick={() =>
                void run(
                  () => api.createProject(name.trim(), slug, idempotencyKey('project')),
                  (created) => {
                    setProjectId(created.projectId);
                    setStep('discover');
                  },
                )
              }
            >
              {busy ? 'Creating…' : 'Create the draft project'}
            </button>
          </div>
        </Panel>
      )}

      {step === 'discover' && projectId && (
        <Panel
          title="2 · Read-only discovery"
          subtitle="Select the installation and repositories. DCC analyses files statically and reports what it found, with the exact path behind each finding."
        >
          <div className="stack" style={{ maxWidth: 640 }}>
            <label className="stack stack--tight">
              <span className="label">GitHub App installation</span>
              <input
                className="btn mono"
                style={{ justifyContent: 'flex-start' }}
                value={installationId}
                onChange={(event) => setInstallationId(event.target.value)}
              />
            </label>
            <label className="stack stack--tight">
              <span className="label">Repositories (one per line, owner/name)</span>
              <textarea
                className="btn mono"
                style={{
                  minHeight: 76,
                  padding: 8,
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                }}
                value={repositories}
                onChange={(event) => setRepositories(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || repositories.trim().length === 0}
              onClick={() =>
                void run(
                  () =>
                    api.discover(
                      projectId,
                      installationId,
                      repositories
                        .split('\n')
                        .map((line) => line.trim())
                        .filter(Boolean),
                      idempotencyKey('discover'),
                    ),
                  (found) => {
                    setDiscovery(found);
                    setDecisions(
                      Object.fromEntries(
                        found.proposals.map((proposal) => [
                          proposal.detectedKey,
                          {
                            detectedKey: proposal.detectedKey,
                            entityType: proposal.entityType,
                            confirmedValue: proposal.proposedValue,
                            accepted: !proposal.requiresConfirmation,
                          },
                        ]),
                      ),
                    );
                    setStep('map');
                  },
                )
              }
            >
              {busy ? 'Analysing…' : 'Run discovery'}
            </button>
          </div>
        </Panel>
      )}

      {step === 'map' && discovery && projectId && (
        <>
          <Panel
            title="Discovery evidence"
            subtitle="Each finding cites the file or provider fact it came from, with a confidence. Nothing here is treated as confirmed."
            actions={<StateChip kind="connection" state="MAPPING_PROPOSED" />}
          >
            <div className="rows">
              {discovery.findings.map((finding) => (
                <div
                  key={finding.key}
                  className="row"
                  style={{ gridTemplateColumns: '1.4fr 1fr auto' }}
                >
                  <div className="row__stack row__cell" data-label="Finding">
                    <span>{finding.fact}</span>
                    <span className="row__sub">{finding.category}</span>
                  </div>
                  <span className="mono small row__cell truncate" data-label="Evidence">
                    {finding.evidencePath}
                  </span>
                  <span className="tiny dim row__cell" data-label="Confidence">
                    {Math.round(finding.confidence * 100)}% · {finding.quality}
                  </span>
                </div>
              ))}
            </div>
            {discovery.unsupported.length > 0 && (
              <>
                <div className="divider" />
                <div className="label">Not supported by this installation</div>
                <ul
                  className="small stack stack--tight"
                  style={{ margin: '6px 0 0', paddingLeft: 16 }}
                >
                  {discovery.unsupported.map((entry) => (
                    <li key={entry}>{entry}</li>
                  ))}
                </ul>
              </>
            )}
          </Panel>

          <Panel
            title="3 · Confirm or correct the mapping"
            subtitle="Detected source, proposed DCC entity, and your decision. Anything ambiguous or high-impact must be confirmed before verification can pass."
          >
            <div className="stack">
              {discovery.proposals.map((proposal) => {
                const decision = decisions[proposal.detectedKey]!;
                return (
                  <div
                    key={proposal.detectedKey}
                    className="card card--toned"
                    data-tone={
                      decision.accepted
                        ? 'success'
                        : proposal.requiresConfirmation
                          ? 'attention'
                          : 'neutral'
                    }
                  >
                    <div className="spread">
                      <div>
                        <span className="label">{proposal.entityType.replace('_', ' ')}</span>
                        <div className="mono small">
                          {proposal.detectedValue} → {decision.confirmedValue}
                        </div>
                      </div>
                      <span className="tiny dim">
                        {Math.round(proposal.confidence * 100)}% confidence
                      </span>
                    </div>
                    <p className="small dim" style={{ marginTop: 6 }}>
                      {proposal.reason}
                    </p>
                    <p className="tiny dim mono">evidence: {proposal.evidencePath}</p>
                    <div className="inline inline--tight" style={{ marginTop: 10 }}>
                      <label className="stack stack--tight" style={{ flex: 1, minWidth: 180 }}>
                        <span className="label">Confirmed value</span>
                        <input
                          className="btn mono"
                          style={{ justifyContent: 'flex-start' }}
                          value={decision.confirmedValue}
                          onChange={(event) =>
                            setDecisions((current) => ({
                              ...current,
                              [proposal.detectedKey]: {
                                ...decision,
                                confirmedValue: event.target.value,
                              },
                            }))
                          }
                        />
                      </label>
                      <label className="inline inline--tight small">
                        <input
                          type="checkbox"
                          checked={decision.accepted}
                          onChange={(event) =>
                            setDecisions((current) => ({
                              ...current,
                              [proposal.detectedKey]: {
                                ...decision,
                                accepted: event.target.checked,
                              },
                            }))
                          }
                        />
                        Confirmed by me
                      </label>
                    </div>
                  </div>
                );
              })}

              <label className="stack stack--tight">
                <span className="label">Reason (recorded in the audit trail)</span>
                <textarea
                  className="btn"
                  style={{
                    minHeight: 56,
                    padding: 8,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                  }}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>

              <button
                type="button"
                className="btn btn--primary"
                disabled={
                  busy ||
                  reason.trim().length < 3 ||
                  discovery.proposals.some(
                    (proposal) =>
                      proposal.requiresConfirmation && !decisions[proposal.detectedKey]?.accepted,
                  )
                }
                onClick={() =>
                  void run(
                    () =>
                      api.confirmMapping(
                        projectId,
                        discovery.connectionId,
                        Object.values(decisions),
                        reason,
                        idempotencyKey('confirm'),
                      ),
                    (confirmed) => {
                      setResult(confirmed);
                      setStep('verify');
                    },
                  )
                }
              >
                {busy ? 'Verifying…' : 'Confirm the mapping and verify'}
              </button>
              {discovery.proposals.some(
                (proposal) =>
                  proposal.requiresConfirmation && !decisions[proposal.detectedKey]?.accepted,
              ) && (
                <p className="tiny dim">
                  One or more high-impact mappings still need your confirmation. DCC will not guess
                  them.
                </p>
              )}
            </div>
          </Panel>
        </>
      )}

      {step === 'verify' && result && projectId && (
        <Panel
          title="4 · Verification"
          subtitle="A mandatory failure means FAILED, an optional failure means DEGRADED, and an unconfirmed mandatory mapping means the connection waits. Only a clean run reaches CONNECTED."
          actions={<StateChip kind="connection" state={result.state} size="lg" />}
        >
          <div className="stack">
            <p className="small">{result.detail}</p>
            <div className="rows">
              {result.verification.map((check) => (
                <div
                  key={check.key}
                  className="row"
                  style={{ gridTemplateColumns: 'auto 1fr auto' }}
                >
                  <span
                    className="chip"
                    data-tone={
                      check.status === 'PASS'
                        ? 'success'
                        : check.status === 'FAIL'
                          ? check.mandatory
                            ? 'critical'
                            : 'risk'
                          : 'attention'
                    }
                  >
                    {check.status}
                  </span>
                  <div className="row__stack row__cell" data-label="Check">
                    <strong>{check.label}</strong>
                    <span className="row__sub">{check.detail}</span>
                    {check.remediation && <span className="row__sub">{check.remediation}</span>}
                  </div>
                  <span className="tiny dim row__cell" data-label="Required">
                    {check.mandatory ? 'mandatory' : 'optional'}
                  </span>
                </div>
              ))}
            </div>
            <div className="inline inline--tight">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => navigate(`/projects/${projectId}/control`)}
              >
                Open the project
              </button>
              <Link className="btn btn--quiet" to="/integrations">
                Connection health
              </Link>
            </div>
          </div>
        </Panel>
      )}
    </>
  );
}
